import AppDialog from '@/components/shared/dialogs/AppDialog';
import { ImperativeDialogRef } from '@/components/shared/dialogs/useImperativeDialogRef';
import { TransactionListCard } from '@/components/transactions/TransactionListCard';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useMatchingTransactions } from '@/hooks/use-matching-transactions';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { Eye, Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

const MatchingTransactionsDialog = forwardRef<
  ImperativeDialogRef,
  { rawRule: AIRuleResponse }
>(({ rawRule }, ref) => {
  const { t } = useTranslation();
  const { transactions, isLoading } = useMatchingTransactions(rawRule);

  return (
    <AppDialog ref={ref}>
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Eye className='w-5 h-5' />
            {t('aiAutomation.matchingTransactions')}
          </DialogTitle>
          <DialogDescription>
            {t('aiAutomation.transactionMatchCount', {
              count: transactions?.length ?? 0,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className='flex-1 overflow-auto space-y-3'>
          {isLoading ? (
            <div className='flex justify-center items-center py-16'>
              <Loader2 className='w-8 h-8 animate-spin text-muted-foreground' />
              <span className='ml-3 text-muted-foreground'>
                {t('common.loading')}
              </span>
            </div>
          ) : transactions.length > 0 ? (
            <TransactionListCard transactions={transactions} />
          ) : (
            <div className='text-center py-8 text-muted-foreground'>
              {t('aiAutomation.noMatchingTransactions')}
            </div>
          )}
        </div>
      </DialogContent>
    </AppDialog>
  );
});
MatchingTransactionsDialog.displayName = 'MatchingTransactionsDialog';
export default MatchingTransactionsDialog;
